/**
 * Configured Axios Instance
 *
 * Creates and configures the shared AxiosInstance with:
 * - Dynamic baseURL resolution
 * - Auth token injection
 * - Orphan trace detection (Sentry)
 * - 401 token refresh + retry
 * - USER_NOT_FOUND auto-sync recovery
 *
 * The instance is consumed by the generated API functions via bindClient().
 */

import axios, { AxiosInstance, AxiosError } from "axios";
import * as Sentry from "@sentry/react-native";
import { Platform } from "react-native";
import { APP_VERSION, getApiUrl } from "../config/constants";
import {
  HTTP_DEFAULT_TIMEOUT_MS,
  HTTP_MAX_RETRIES,
  HTTP_RETRY_BASE_MS,
  HTTP_RETRY_FACTOR,
  HTTP_RETRY_JITTER,
} from "../config/timeouts";
import { getAuthProvider } from "../auth";
import { createLogger } from "../utils/logger";
import { freshnessManager } from "./freshnessManager";
import { PULL_ENDPOINT_DOMAIN_MAP, VERSION_HEADER } from "../generated/sync/sync-config";
import type { ApiErrorResponse } from "../generated/api/http-contracts";
import { initInstallationId, getInstallationIdSync } from "../utils/installationId";
import { getCurrentIntent } from "../dev/intentTag";

const log = createLogger("API");

let syncRecoveryAttempted = false;
let tokenRefreshInProgress: Promise<boolean> | null = null;

/**
 * Derive a `METHOD /path` key for a response from its underlying request,
 * matching the format used in `PULL_ENDPOINT_DOMAIN_MAP` keys.
 *
 * The codegen emits map keys with the FULL backend-facing path
 * (e.g. `GET /api/user/me`), but Fluencia's axios config puts the
 * `/api` prefix in `baseURL` and the bare resource path in `config.url`
 * (e.g. `/user/me`). Concatenating them mirrors how axios itself
 * composes the request URL.
 *
 * Normalisation:
 *   - Method uppercased.
 *   - URL composed as `baseURL + url`, then the pathname extracted so
 *     scheme/host/port drop out (works whether baseURL is host-rooted
 *     `https://x` or path-rooted `/api`).
 *   - Query string stripped, trailing slash trimmed (keeps the root `/`
 *     intact), leading `/` enforced.
 *
 * Returns null when method or URL is missing — axios responses generated
 * from interceptor-only error paths may omit either.
 *
 * Path-param substitution is intentionally NOT done here: this codepath
 * only checks for an exact match against domain-pull endpoints, all of
 * which are static (`/api/user/me`, `/api/courses`, etc.). If a domain
 * adds a parameterised pull endpoint in the future, this helper will
 * need to fold the param shape into the lookup — but until then, the
 * extra logic would be dead code.
 */
export function endpointForResponse(
  response: {
    config?: { method?: string; url?: string; baseURL?: string };
  },
): string | null {
  const method = response.config?.method?.toUpperCase();
  const rawUrl = response.config?.url;
  if (!method || !rawUrl) return null;

  // Mirror axios's own URL composition: if `url` is absolute
  // (`http://…`), use it verbatim; otherwise concatenate
  // `baseURL + url` with slash normalisation between them.
  const isAbsolute = /^https?:\/\//i.test(rawUrl);
  let combined: string;
  if (isAbsolute) {
    combined = rawUrl;
  } else {
    const base = (response.config?.baseURL ?? "").replace(/\/+$/, "");
    const tail = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    combined = base + tail;
  }

  // Pull just the path portion. `URL` is the safest parse; when the
  // input has no scheme (baseURL is itself a relative path like
  // `/api`), we fall back to treating the combined string as the path.
  let path: string;
  try {
    path = new URL(combined).pathname;
  } catch {
    path = combined;
  }

  const queryIdx = path.indexOf("?");
  if (queryIdx >= 0) path = path.slice(0, queryIdx);
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return `${method} ${path}`;
}

/**
 * Attempt to refresh the auth token.
 * Deduplicates concurrent refresh requests so only one network call is made.
 */
async function refreshToken(): Promise<boolean> {
  if (tokenRefreshInProgress) {
    return tokenRefreshInProgress;
  }

  tokenRefreshInProgress = (async () => {
    try {
      const refreshed = await getAuthProvider().refreshSession();
      if (!refreshed) {
        log.warn("Token refresh failed");
        return false;
      }
      log.info("Token refreshed successfully");
      return true;
    } catch (err) {
      log.warn("Token refresh threw", err);
      return false;
    } finally {
      tokenRefreshInProgress = null;
    }
  })();

  return tokenRefreshInProgress;
}

/**
 * Extra bookkeeping we hang off the Axios request config. Kept distinct from
 * the auth-refresh (`_retryAfterRefresh`) and sync-recovery flags so the three
 * recovery paths never clobber one another's counters.
 */
export type RetryableConfig = NonNullable<AxiosError["config"]> & {
  /** Number of transport retries already spent on this request. */
  _netRetryCount?: number;
  /** Opt-out for callers that manage their own retry (none today). */
  _noRetry?: boolean;
};

/** HTTP methods safe to retry automatically without risking a double-submit. */
const IDEMPOTENT_METHODS = new Set(["get", "head"]);

/**
 * Decide whether a failed request should be transport-retried.
 *
 * Retries ONLY idempotent methods, ONLY on transport-ish failures (timeout,
 * network error with no response, or a 502/503/504 gateway status the server
 * didn't author). Never retries 4xx, nor a 5xx that carried a structured
 * `ApiErrorResponse` body (that's a real domain failure, not a dead socket).
 *
 * Exported for unit testing (mirrors `endpointForResponse`) — the full Axios
 * instance is too platform-entangled to exercise under jest, so the branching
 * decision logic is tested in isolation.
 */
export function shouldRetryTransport(error: AxiosError, config: RetryableConfig): boolean {
  if (config._noRetry) return false;

  const method = config.method?.toLowerCase();
  if (!method || !IDEMPOTENT_METHODS.has(method)) return false;

  const attempts = config._netRetryCount ?? 0;
  if (attempts >= HTTP_MAX_RETRIES) return false;

  // No response at all — timeout or network error → likely a stale/dead socket.
  if (!error.response) {
    return true;
  }

  // Gateway-level 5xx with no meaningful body → transient, safe to retry.
  const status = error.response.status;
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }

  return false;
}

/** Backoff before the next retry: exponential with ±jitter (SyncQueue shape). */
export function retryDelayMs(attempt: number): number {
  const base = HTTP_RETRY_BASE_MS * Math.pow(HTTP_RETRY_FACTOR, attempt);
  const jitter = base * HTTP_RETRY_JITTER * (Math.random() * 2 - 1);
  return Math.max(0, base + jitter);
}

function createApiClient(): AxiosInstance {
  // Pre-load installation ID so the sync getter is ready for interceptors
  initInstallationId();

  const client = axios.create({
    // Fail-fast default; genuinely-slow calls (e.g. analyze) override
    // `timeout` per-request. See config/timeouts.ts + network-resilience spec.
    timeout: HTTP_DEFAULT_TIMEOUT_MS,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Dynamically set baseURL on each request to handle late API_URL resolution
  client.interceptors.request.use((config) => {
    config.baseURL = getApiUrl();
    return config;
  });

  // Add auth token to requests automatically
  client.interceptors.request.use(async (config) => {
    const token = await getAuthProvider().getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // E2E intent attribution (dev only): tag every outbound request with
  // the active intent so the traffic proxy can attribute the call
  // to the correct intent regardless of wire-time race conditions.
  // The runner pushes the current intent via deep link before each
  // dispatchDo; production builds tree-shake this entirely since
  // `__DEV__` is false. See mobile/src/dev/intentTag.ts.
  if (__DEV__) {
    client.interceptors.request.use((config) => {
      const intent = getCurrentIntent();
      if (intent) {
        config.headers["x-e2e-intent"] = intent;
      }
      return config;
    });
  }

  // Detect orphan traces (requests made without an active span)
  client.interceptors.request.use((config) => {
    const activeSpan = Sentry.getActiveSpan();

    if (!activeSpan) {
      const method = config.method?.toUpperCase() || "UNKNOWN";
      const url = config.url?.split("?")[0] || "unknown";

      Sentry.captureMessage(`Orphan trace detected: ${method} ${url}`, {
        level: "warning",
        fingerprint: ["orphan-trace", method, url],
        tags: {
          orphan_trace: "true",
          "http.method": method,
          "http.url": url,
        },
        extra: {
          description:
            "This request was made without an active span. Consider wrapping with startNewTrace() if user-triggered.",
        },
      });
    }

    return config;
  });

  // Handle errors globally
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<unknown>) => {
      const originalRequest = error.config as (typeof error.config) & { _retryAfterRefresh?: boolean };

      // 401 — attempt token refresh, then retry once
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retryAfterRefresh
      ) {
        originalRequest._retryAfterRefresh = true;

        const refreshed = await refreshToken();
        if (refreshed) {
          const token = await getAuthProvider().getAccessToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return client.request(originalRequest);
        }

        // Refresh failed — force logout (only if authenticated, not during startup)
        const { useAuthStore } = await import("../store/authStore");
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          log.warn("Token refresh failed, forcing logout");
          useAuthStore.getState().logout();
        } else {
          log.warn("Token refresh failed during startup, skipping force logout");
        }
        return Promise.reject(error);
      }

      // Auto-recovery: if backend says USER_NOT_FOUND, sync and retry once
      if (
        error.response?.status === 404 &&
        (error.response?.data as ApiErrorResponse)?.error?.code === 'USER_NOT_FOUND' &&
        !syncRecoveryAttempted
      ) {
        syncRecoveryAttempted = true;
        try {
          // Inline sync call to avoid circular dependency with api.ts
          await client.post("/user/sync", {});
          return client.request(error.config!);
        } catch {
          return Promise.reject(error);
        }
      }

      // Transport retry: a GET/HEAD that timed out or hit a network error is
      // likely stuck on a stale pooled TCP socket after a network transition.
      // Retrying establishes a fresh connection (the OS won't reuse the one
      // that just aborted). On exhaustion we reject with the ORIGINAL error so
      // wrapError classifies it exactly as before.
      const retryConfig = originalRequest as RetryableConfig | undefined;
      if (retryConfig && shouldRetryTransport(error, retryConfig)) {
        const attempt = retryConfig._netRetryCount ?? 0;
        retryConfig._netRetryCount = attempt + 1;
        const delay = retryDelayMs(attempt);
        log.info("Retrying request after transport error", {
          method: retryConfig.method,
          url: retryConfig.url,
          attempt: attempt + 1,
          maxRetries: HTTP_MAX_RETRIES,
          delayMs: Math.round(delay),
          code: error.code,
        });
        await new Promise((resolve) => setTimeout(resolve, delay));
        return client.request(retryConfig);
      }

      return Promise.reject(error);
    }
  );

  // Send client's known data version on every request
  client.interceptors.request.use((config) => {
    config.headers[VERSION_HEADER] = freshnessManager.getUserDataVersion();
    config.headers['X-App-Version'] = APP_VERSION;
    config.headers['X-Platform'] = Platform.OS;
    config.headers['X-Device-Id'] = getInstallationIdSync();
    return config;
  });

  // Read server data version + stale domains from every response
  client.interceptors.response.use((response) => {

    const serverVersion = Number(response.headers[VERSION_HEADER.toLowerCase()]);
    if (!serverVersion) return response; // header absent — do nothing

    const localVersion = freshnessManager.getUserDataVersion();
    if (serverVersion > localVersion) {
      const staleHeader = response.headers['x-stale-domains'] as string | undefined;
      if (staleHeader) {
        const staleDomains = freshnessManager.parseStaleDomains(staleHeader);
        // Skip the domain whose endpoint THIS response is for — the
        // response itself IS the refresh for that domain, so kicking
        // off a `refreshDomains` for it would fire a redundant second
        // request via the registered callback (e.g. a fresh
        // `fetchProfile()` → second `GET /me`). Cross-domain stale
        // signals (e.g. `/me` flagging `courses` stale) keep firing.
        //
        // PULL_ENDPOINT_DOMAIN_MAP is codegen-derived from sync.yaml's
        // `domain.pull.endpoint` — stays in sync with the DSL by
        // construction. See its docstring for the full hazard model.
        const responseEndpoint = endpointForResponse(response);
        const selfRefreshedDomain = responseEndpoint
          ? PULL_ENDPOINT_DOMAIN_MAP[responseEndpoint]
          : undefined;
        const domainsToRefresh = selfRefreshedDomain
          ? staleDomains.filter((d) => d !== selfRefreshedDomain)
          : staleDomains;
        if (domainsToRefresh.length > 0) {
          freshnessManager.refreshDomains(domainsToRefresh, serverVersion);
        }
      }
      freshnessManager.setUserDataVersion(serverVersion);
    }

    return response;
  });

  // Dev-only: stream HTTP calls to dev-dashboard
  if (__DEV__) {
    const { notifyHttp } = require('../dev/storage-observer');
    const startTimes = new WeakMap<object, number>();

    client.interceptors.request.use((config) => {
      startTimes.set(config, Date.now());
      return config;
    });

    client.interceptors.response.use(
      (response) => {
        const start = startTimes.get(response.config) || Date.now();
        const url = (response.config.url || '').replace(response.config.baseURL || '', '');
        notifyHttp(
          (response.config.method || 'GET').toUpperCase(),
          url,
          response.status,
          Date.now() - start,
          response.config.data ? tryParseJson(response.config.data) : null,
          response.data,
          null,
        );
        return response;
      },
      (error: AxiosError) => {
        const config = error.config;
        const start = config ? startTimes.get(config) || Date.now() : Date.now();
        const url = config ? (config.url || '').replace(config.baseURL || '', '') : 'unknown';
        notifyHttp(
          (config?.method || 'GET').toUpperCase(),
          url,
          error.response?.status ?? null,
          Date.now() - start,
          config?.data ? tryParseJson(config.data) : null,
          error.response?.data ?? null,
          error.message,
        );
        return Promise.reject(error);
      },
    );
  }

  return client;
}

function tryParseJson(data: unknown): unknown {
  if (typeof data === 'string') {
    try { return JSON.parse(data); } catch { return data; }
  }
  return data;
}

export const apiClient = createApiClient();

/**
 * Reset sync recovery flag (call on logout).
 * Allows the next session to attempt auto-recovery if needed.
 */
export function resetSyncRecovery() {
  syncRecoveryAttempted = false;
}
